import { Checkbox, FormControlLabel } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { StandardModal } from "../table-page/StandardModal.tsx";
import { PasswordField } from "../auth/PasswordField.tsx";
import { isPrivilegedRoleOption } from "../../../utils/appRole.js";
import { resolveUserFormRoleId } from "../api/adminUsersRolesClient.ts";
import { fetchCameras } from "../store/localUserRoleStore.tsx";
import type { UserListItem, UserRoleOption } from "../types.tsx";

export type UserFormValues = {
  firstName: string;
  lastName: string;
  roleId: string;
  email: string;
  password: string;
  cameraIds: string[];
};

const empty: UserFormValues = {
  firstName: "",
  lastName: "",
  roleId: "",
  email: "",
  password: "",
  cameraIds: [],
};

function splitFullName(fullName: string | null | undefined): { first: string; last: string } {
  const s = (fullName ?? "").trim();
  if (!s) return { first: "", last: "" };
  const i = s.indexOf(" ");
  if (i === -1) return { first: s, last: "" };
  return { first: s.slice(0, i).trim(), last: s.slice(i + 1).trim() };
}

function fromUser(u: UserListItem): UserFormValues {
  const { first, last } = splitFullName(u.fullName);
  return {
    firstName: first,
    lastName: last,
    roleId: u.businessRoleId ?? "",
    email: u.email,
    password: "",
    cameraIds: u.cameraIds ?? [],
  };
}

export type UserFormModalProps = {
  open: boolean;
  mode: "create" | "edit";
  user?: UserListItem | null;
  roleOptions: UserRoleOption[];
  camerasRequired?: boolean;
  loading?: boolean;
  serverError?: string | null;
  fieldErrors?: Partial<Record<keyof UserFormValues | "form", string>>;
  onClose: () => void;
  onSubmit: (values: UserFormValues) => void;
  onRoleIdChange?: (roleId: string) => void;
};

export function UserFormModal({
  open,
  mode,
  user,
  roleOptions,
  camerasRequired = true,
  loading,
  serverError,
  fieldErrors,
  onClose,
  onSubmit,
  onRoleIdChange,
}: UserFormModalProps) {
  const [values, setValues] = useState<UserFormValues>(empty);
  const [cameras, setCameras] = useState<{ id: string; name: string }[]>([]);
  const [cameraSearch, setCameraSearch] = useState("");
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [camerasLoadError, setCamerasLoadError] = useState<string | null>(null);

  const showCameraPicker = camerasRequired;

  useEffect(() => {
    if (!open) return;
    setCameraSearch("");
    if (mode === "edit" && user) {
      const base = fromUser(user);
      setValues({
        ...base,
        roleId: resolveUserFormRoleId(user, roleOptions) || base.roleId,
      });
    } else {
      setValues({ ...empty, roleId: roleOptions[0]?.id ?? "", cameraIds: [] });
    }
  }, [open, mode, roleOptions, user]);

  useEffect(() => {
    if (!open || !showCameraPicker) {
      setCameras([]);
      setCamerasLoadError(null);
      return;
    }

    let cancelled = false;
    const t = window.setTimeout(() => {
      setCamerasLoading(true);
      void fetchCameras(cameraSearch)
        .then((rows) => {
          if (!cancelled) {
            setCameras(rows.map((c) => ({ id: c.id, name: c.name })));
            setCamerasLoadError(null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setCameras([]);
            setCamerasLoadError("Unable to load cameras.");
          }
        })
        .finally(() => {
          if (!cancelled) setCamerasLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [cameraSearch, open, showCameraPicker]);

  const title = mode === "create" ? "Add User" : "Edit User";
  const primaryLabel = mode === "create" ? "Save" : "Update";

  const footer = useMemo(
    () => (
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="ds-btn ds-btn--ghost" onClick={onClose} disabled={loading}>
          Cancel
        </button>
        <button
          type="button"
          className="ds-btn ds-btn--primary"
          disabled={loading}
          onClick={() => onSubmit(values)}
        >
          {primaryLabel}
        </button>
      </div>
    ),
    [loading, onClose, onSubmit, primaryLabel, values],
  );

  const err = (k: keyof UserFormValues | "form") => fieldErrors?.[k];

  const toggleCamera = (cameraId: string) => {
    setValues((v) => {
      const set = new Set(v.cameraIds);
      if (set.has(cameraId)) set.delete(cameraId);
      else set.add(cameraId);
      return { ...v, cameraIds: Array.from(set) };
    });
  };

  const allVisibleSelected =
    cameras.length > 0 && cameras.every((cam) => values.cameraIds.includes(cam.id));
  const someVisibleSelected = cameras.some((cam) => values.cameraIds.includes(cam.id));

  const toggleSelectAllVisible = () => {
    const visibleIds = new Set(cameras.map((c) => c.id));
    if (allVisibleSelected) {
      setValues((v) => ({
        ...v,
        cameraIds: v.cameraIds.filter((id) => !visibleIds.has(id)),
      }));
    } else {
      setValues((v) => {
        const merged = new Set(v.cameraIds);
        for (const id of visibleIds) merged.add(id);
        return { ...v, cameraIds: Array.from(merged) };
      });
    }
  };

  return (
    <StandardModal open={open} title={title} onClose={onClose} footer={footer} loading={loading}>
      <div className="ds-stack ds-stack--loose" style={{ width: "100%" }}>
        {serverError || err("form") ? (
          <p className="ds-error-inline" style={{ margin: 0 }}>
            {serverError ?? err("form")}
          </p>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
          <label className="ds-field" style={{ marginBottom: 0 }}>
            <span className="ds-field__label">First Name</span>
            <input
              className="ds-input"
              placeholder="Enter First Name"
              value={values.firstName}
              onChange={(e) => setValues((v) => ({ ...v, firstName: e.target.value }))}
              autoComplete="given-name"
            />
            {err("firstName") ? <span className="ds-field__error">{err("firstName")}</span> : null}
          </label>
          <label className="ds-field" style={{ marginBottom: 0 }}>
            <span className="ds-field__label">Last Name</span>
            <input
              className="ds-input"
              placeholder="Enter Last Name"
              value={values.lastName}
              onChange={(e) => setValues((v) => ({ ...v, lastName: e.target.value }))}
              autoComplete="family-name"
            />
            {err("lastName") ? <span className="ds-field__error">{err("lastName")}</span> : null}
          </label>
        </div>

        <label className="ds-field">
          <span className="ds-field__label">Role</span>
          <select
            className="ds-input"
            value={values.roleId}
            onChange={(e) => {
              const roleId = e.target.value;
              setValues((v) => ({
                ...v,
                roleId,
                cameraIds: isPrivilegedRoleOption(roleId, roleOptions) ? [] : v.cameraIds,
              }));
              onRoleIdChange?.(roleId);
            }}
          >
            {roleOptions.length === 0 ? <option value="">No active roles available</option> : null}
            {roleOptions.map((role) => (
              <option key={role.id} value={role.id}>
                {role.role}
              </option>
            ))}
          </select>
          {err("roleId") ? <span className="ds-field__error">{err("roleId")}</span> : null}
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
          <label className="ds-field" style={{ marginBottom: 0 }}>
            <span className="ds-field__label">Email</span>
            <input
              className="ds-input"
              type="email"
              placeholder="Enter Email ID"
              value={values.email}
              onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
              autoComplete="email"
            />
            {err("email") ? <span className="ds-field__error">{err("email")}</span> : null}
          </label>
          <div style={{ marginBottom: 0 }}>
            <PasswordField
              label="Password"
              placeholder={mode === "edit" ? "Leave blank to keep current password" : "Enter Password"}
              value={values.password}
              onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
              autoComplete="new-password"
            />
            {err("password") ? <span className="ds-field__error">{err("password")}</span> : null}
          </div>
        </div>

        {showCameraPicker ? (
          <label className="ds-field">
            <span className="ds-field__label">Cameras (view access)</span>
            <input
              className="ds-input"
              placeholder="Search cameras..."
              value={cameraSearch}
              onChange={(e) => setCameraSearch(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <div className="ds-camera-picker">
              <div className="ds-camera-picker__toolbar">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={camerasLoading || cameras.length === 0}
                    />
                  }
                  label="Select all"
                />
                {camerasLoading ? (
                  <span className="ds-text-secondary" style={{ fontSize: 12 }}>
                    Loading…
                  </span>
                ) : null}
              </div>
              <div className="ds-camera-picker__list">
                {camerasLoadError ? (
                  <span className="ds-error-inline">{camerasLoadError}</span>
                ) : cameras.length === 0 && !camerasLoading ? (
                  <span className="ds-text-secondary">No cameras found.</span>
                ) : (
                  cameras.map((cam) => (
                    <label key={cam.id} className="ds-camera-picker__row">
                      <input
                        type="checkbox"
                        checked={values.cameraIds.includes(cam.id)}
                        onChange={() => toggleCamera(cam.id)}
                      />
                      <span>{cam.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            {err("cameraIds") ? <span className="ds-field__error">{err("cameraIds")}</span> : null}
          </label>
        ) : (
          <p className="ds-text-secondary" style={{ margin: 0, fontSize: 13 }}>
            Admin users have access to all cameras; no per-camera assignment needed.
          </p>
        )}
      </div>
    </StandardModal>
  );
}
